// Shared primitives for KWF Admin pages — ported to the KWF design artifact.
// Every component wraps itself in / applies an `ad-*` class so the host app's
// Bootstrap cascade can't shadow colours, backgrounds or spacing. The real
// styling lives in admin-ui.css; this file just assembles DOM + classes.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { fetchApi } from "../../utlis/axios";
import "./admin-ui.css";

export function useAdminApi() {
  const access = useSelector((s) => s.authSlice.userInfo?.access);
  const auth = { Authorization: `Bearer ${access}` };
  return {
    get: (url, cfg = {}) => fetchApi.get(url, { ...cfg, headers: { ...(cfg.headers || {}), ...auth } }),
    post: (url, data, cfg = {}) => fetchApi.post(url, data, { ...cfg, headers: { ...(cfg.headers || {}), ...auth } }),
    patch: (url, data, cfg = {}) => fetchApi.patch(url, data, { ...cfg, headers: { ...(cfg.headers || {}), ...auth } }),
    del: (url, cfg = {}) => fetchApi.delete(url, { ...cfg, headers: { ...(cfg.headers || {}), ...auth } }),
  };
}

/* ─────────────────────────────────────────────
 * Icons — compact line set used across admin UI.
 * ───────────────────────────────────────────── */
const _s = { fill: "none", stroke: "currentColor", strokeWidth: 1.75, strokeLinecap: "round", strokeLinejoin: "round", viewBox: "0 0 24 24" };
export const Icon = {
  check:   (p) => <svg {..._s} {...p}><path d="m4 12 5 5 11-11"/></svg>,
  x:       (p) => <svg {..._s} {...p}><path d="M6 6l12 12M18 6 6 18"/></svg>,
  search:  (p) => <svg {..._s} {...p}><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>,
  plus:    (p) => <svg {..._s} {...p}><path d="M12 5v14M5 12h14"/></svg>,
  trash:   (p) => <svg {..._s} {...p}><path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13"/></svg>,
  edit:    (p) => <svg {..._s} {...p}><path d="M4 20h4L19 9l-4-4L4 16v4Z"/><path d="m14 6 4 4"/></svg>,
  key:     (p) => <svg {..._s} {...p}><circle cx="8" cy="15" r="4"/><path d="m11 12 9-9m-3 3 3 3"/></svg>,
  download:(p) => <svg {..._s} {...p}><path d="M12 4v12m0 0-4-4m4 4 4-4M4 20h16"/></svg>,
  users:   (p) => <svg {..._s} {...p}><circle cx="9" cy="8" r="3.5"/><path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6"/><circle cx="17" cy="7" r="2.5"/><path d="M15.5 14c2.7.3 5 2.4 5 5"/></svg>,
  userPlus:(p) => <svg {..._s} {...p}><circle cx="9" cy="8" r="3.5"/><path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6"/><path d="M18 10v6M15 13h6"/></svg>,
  hospital:(p) => <svg {..._s} {...p}><rect x="4" y="4" width="16" height="17" rx="2"/><path d="M12 8v6M9 11h6"/><path d="M4 21h16"/></svg>,
  apps:    (p) => <svg {..._s} {...p}><rect x="3.5" y="3.5" width="7" height="7" rx="1.5"/><rect x="13.5" y="3.5" width="7" height="7" rx="1.5"/><rect x="3.5" y="13.5" width="7" height="7" rx="1.5"/><rect x="13.5" y="13.5" width="7" height="7" rx="1.5"/></svg>,
  info:    (p) => <svg {..._s} {...p}><circle cx="12" cy="12" r="9"/><path d="M12 11v6M12 8v.5"/></svg>,
  alertTri:(p) => <svg {..._s} {...p}><path d="M12 3 2 20h20L12 3Z"/><path d="M12 10v4M12 17v.5"/></svg>,
  chevR:   (p) => <svg {..._s} {...p}><path d="m9 5 7 7-7 7"/></svg>,
  chevD:   (p) => <svg {..._s} {...p}><path d="m5 9 7 7 7-7"/></svg>,
  cross:   (p) => <svg {..._s} {...p}><path d="M12 4v16M4 12h16"/></svg>,
  logOut:  (p) => <svg {..._s} {...p}><path d="M14 4H6v16h8"/><path d="M20 12H10m0 0 3 3m-3-3 3-3"/></svg>,
  package: (p) => <svg {..._s} {...p}><path d="M12 2 3 7v10l9 5 9-5V7l-9-5Z"/><path d="M3 7l9 5 9-5M12 12v10"/></svg>,
  bug:     (p) => <svg {..._s} {...p}><rect x="7" y="8" width="10" height="10" rx="5"/><path d="M12 8V4M8 4l2 3M16 4l-2 3M4 13h3m10 0h3M5 18l2-2m10 2-2-2M5 8l2 2m10-2-2 2"/></svg>,
  file:    (p) => <svg {..._s} {...p}><path d="M5 3h9l5 5v13H5z"/><path d="M14 3v5h5"/></svg>,
  monitor: (p) => <svg {..._s} {...p}><rect x="3" y="4" width="18" height="12" rx="2"/><path d="M9 20h6M12 16v4"/></svg>,
  activity:(p) => <svg {..._s} {...p}><path d="M3 12h4l2-7 4 14 2-7h6"/></svg>,
  wallet:  (p) => <svg {..._s} {...p}><rect x="3" y="6" width="18" height="13" rx="2"/><path d="M3 10h18M16 14h2"/></svg>,
  cloud:   (p) => <svg {..._s} {...p}><path d="M7 18h11a4 4 0 0 0 .4-7.98A6 6 0 0 0 6.5 9.5 4 4 0 0 0 7 18Z"/></svg>,
  shield:  (p) => <svg {..._s} {...p}><path d="M12 3 4 6v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V6l-8-3Z"/></svg>,
  database:(p) => <svg {..._s} {...p}><ellipse cx="12" cy="5.5" rx="8" ry="2.5"/><path d="M4 5.5v6c0 1.4 3.6 2.5 8 2.5s8-1.1 8-2.5v-6M4 11.5v6c0 1.4 3.6 2.5 8 2.5s8-1.1 8-2.5v-6"/></svg>,
  refresh: (p) => <svg {..._s} {...p}><path d="M20 11A8 8 0 0 0 6 7m-2-3v5h5M4 13a8 8 0 0 0 14 4m2 3v-5h-5"/></svg>,
  copy:    (p) => <svg {..._s} {...p}><rect x="8" y="8" width="12" height="12" rx="2"/><path d="M4 16V5a1 1 0 0 1 1-1h11"/></svg>,
};

/* ─────────────────────────────────────────────
 * Shell wrapper — put at the top of every admin
 * page so our scoped CSS variables + font apply.
 * ───────────────────────────────────────────── */
export function PageShell({ title, subtitle, actions, breadcrumb, children }) {
  return (
    <div className="ad-page">
      {breadcrumb && breadcrumb.length > 0 && (
        <nav className="ad-crumbs">
          {breadcrumb.map((c, i) => (
            <React.Fragment key={i}>
              {i > 0 && <span className="sep">›</span>}
              {c.to ? <a href={c.to}>{c.label}</a> : <span style={{ color: "var(--fg-2)" }}>{c.label}</span>}
            </React.Fragment>
          ))}
        </nav>
      )}
      <div className="ad-ph">
        <div>
          <h1>{title}</h1>
          {subtitle && <p>{subtitle}</p>}
        </div>
        {actions && <div className="ad-ph-actions">{actions}</div>}
      </div>
      {children}
    </div>
  );
}

/* ─────────────────────────────────────────────
 * Button + icon button
 * ───────────────────────────────────────────── */
export function ActionButton({ onClick, children, variant = "primary", type = "button", disabled, title, icon, size }) {
  const cls = `ad-btn ${variant === "primary" ? "primary" : variant === "danger" ? "danger-solid" : variant === "danger-outline" ? "danger" : "ghost"}${size === "sm" ? " sm" : ""}`;
  return (
    <button type={type} className={cls} onClick={onClick} disabled={disabled} title={title}>
      {icon}{children}
    </button>
  );
}

export function IconButton({ onClick, children, title, tone = "neutral", disabled, type = "button" }) {
  const cls = `ad-ib${tone === "danger" ? " danger" : tone === "brand" ? " brand" : ""}`;
  return (
    <button type={type} className={cls} onClick={onClick} disabled={disabled} title={title}>
      {children}
    </button>
  );
}

/* ─────────────────────────────────────────────
 * Loading / error / empty
 * ───────────────────────────────────────────── */
export function LoadingBox({ text = "Loading…" }) {
  return (
    <div className="ad-loading">
      <span className="spin" />
      {text}
    </div>
  );
}

export function ErrorBox({ error }) {
  return (
    <div className="ad-err">
      {String(error?.response?.data?.detail || error?.response?.data?.error || error?.message || error)}
    </div>
  );
}

export function EmptyBox({ text = "Nothing to show yet.", title, icon }) {
  const I = icon || Icon.database;
  return (
    <div className="ad-empty">
      <div className="ad-empty-ic"><I /></div>
      <h4>{title || "Nothing here"}</h4>
      {text && <p>{text}</p>}
    </div>
  );
}

/* ─────────────────────────────────────────────
 * Note (dismissable info / warn banner)
 * ───────────────────────────────────────────── */
export function InfoBanner({ children, variant = "info", dismissable = true }) {
  const [open, setOpen] = useState(true);
  if (!open) return null;
  return (
    <div className={`ad-note${variant === "warn" ? " warn" : ""}`}>
      {variant === "warn" ? <Icon.alertTri /> : <Icon.info />}
      <div>{children}</div>
      {dismissable && <button className="x" onClick={() => setOpen(false)} title="Dismiss"><Icon.x /></button>}
    </div>
  );
}
export const Note = InfoBanner;

/* ─────────────────────────────────────────────
 * Pills + status
 * ───────────────────────────────────────────── */
export function Pill({ children, tone, kind, dot, title }) {
  // Accept either `tone` (modern) or `kind` (artifact wording).
  const k = kind || tone || "neutral";
  return <span className={`pill ${k}`} title={title}>{dot && <i />}{children}</span>;
}

export function StatusPill({ status }) {
  const m = {
    active:      ["ok",   "Active"],
    flagged:     ["warn", "Flagged"],
    revoked:     ["bad",  "Revoked"],
    ready:       ["ok",   "Ready"],
    suspended:   ["bad",  "Suspended"],
    new:         ["bad",  "New"],
    in_progress: ["warn", "In progress"],
    resolved:    ["ok",   "Resolved"],
    pending:     ["warn", "Pending"],
    replaced:    ["neutral", "Replaced"],
  }[status] || ["neutral", status || "—"];
  return <Pill kind={m[0]} dot>{m[1]}</Pill>;
}

/* ─────────────────────────────────────────────
 * Tabs
 * ───────────────────────────────────────────── */
export function TabBar({ tabs, active, onChange }) {
  return (
    <div className="ad-tabs" role="tablist">
      {tabs.map((t) => (
        <button
          key={t.key}
          role="tab"
          className={`ad-tab${active === t.key ? " active" : ""}`}
          onClick={() => onChange(t.key)}
        >
          {t.icon && <t.icon />}
          {t.label}
          {typeof t.count === "number" && <span className="cnt">{t.count}</span>}
        </button>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────
 * Stats
 * ───────────────────────────────────────────── */
export function Stat({ icon, label, value, sub, tone = "teal", onClick }) {
  const palette = {
    teal:   ["var(--brand-50)", "var(--brand-600)"],
    blue:   ["var(--info-bg)",  "var(--info)"],
    green:  ["var(--success-bg)", "var(--success)"],
    amber:  ["var(--warning-bg)", "var(--warning)"],
    violet: ["#F3E8FF", "#7C3AED"],
    red:    ["var(--danger-bg)", "var(--danger)"],
  }[tone] || ["var(--brand-50)", "var(--brand-600)"];
  const I = icon || Icon.activity;
  const Wrap = onClick ? "button" : "div";
  return (
    <Wrap type={onClick ? "button" : undefined} className={`ad-stat${onClick ? " click" : ""}`} onClick={onClick}>
      <div className="ad-stat-ic" style={{ background: palette[0], color: palette[1] }}><I /></div>
      <div style={{ minWidth: 0 }}>
        <div className="ad-stat-l">{label}</div>
        <div className="ad-stat-v">{value ?? "—"}{sub && <small> {sub}</small>}</div>
      </div>
    </Wrap>
  );
}

/* ─────────────────────────────────────────────
 * Data table — shared primitive
 * ───────────────────────────────────────────── */
export function DataTable({ columns, rows, rowKey = "id", onRowClick, emptyText = "No rows." }) {
  if (!rows || rows.length === 0) return <EmptyBox text={emptyText} />;
  return (
    <div className="ad-tbl-wrap">
      <table className="ad-tbl">
        <thead>
          <tr>
            {columns.map((c, i) => (
              <th key={i} className={c.align === "right" ? "r" : undefined}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr
              key={row[rowKey] ?? idx}
              className={onRowClick ? "click" : undefined}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
              {columns.map((c, i) => (
                <td key={i} className={c.align === "right" ? "r" : undefined}>
                  {c.render ? c.render(row) : row[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ─────────────────────────────────────────────
 * Modal + confirm dialog
 * ───────────────────────────────────────────── */
export function Modal({ title, icon, desc, onClose, children, footer, size }) {
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose && onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  const I = icon;
  return (
    <div className="ad-modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}>
      <div className={`ad-modal${size === "sm" ? " sm" : ""}`}>
        <header className="ad-modal-head">
          <h2 className="ad-modal-title">
            {I && <span className="ad-modal-title-icon"><I /></span>}
            {title}
          </h2>
          <button className="ad-modal-close" onClick={onClose} aria-label="Close"><Icon.x /></button>
        </header>
        <div className="ad-modal-body">
          {desc && <p className="ad-modal-desc">{desc}</p>}
          {children}
        </div>
        {footer && <footer className="ad-modal-foot">{footer}</footer>}
      </div>
    </div>
  );
}

/**
 * Confirm hook — styled destructive confirmation modal.
 *
 *   const [confirm, confirmModal] = useConfirmDialog();
 *   if (await confirm({ title, body, detail, confirmLabel, destructive })) { … }
 *   // render {confirmModal} once
 */
export function useConfirmDialog() {
  const [state, setState] = useState(null);
  const confirm = (opts) => new Promise((resolve) => setState({ opts, resolve }));
  const close = (result) => { if (state) state.resolve(result); setState(null); };
  const modal = state ? (
    <Modal
      title={state.opts.title || "Confirm"}
      icon={state.opts.destructive ? Icon.alertTri : Icon.info}
      onClose={() => close(false)}
      size="sm"
      footer={
        <>
          <button className="ad-btn ghost" onClick={() => close(false)}>{state.opts.cancelLabel || "Cancel"}</button>
          <button className={state.opts.destructive ? "ad-btn danger-solid" : "ad-btn primary"} onClick={() => close(true)}>
            {state.opts.destructive && <Icon.trash />}
            {state.opts.confirmLabel || "Confirm"}
          </button>
        </>
      }
    >
      <p style={{ margin: 0, fontSize: 14, color: "var(--fg-2)", lineHeight: 1.55 }}>{state.opts.body}</p>
      {state.opts.detail && (
        <div style={{ marginTop: 12, fontSize: 12.5, color: "var(--fg-3)", background: "var(--bg-2)", border: "1px solid var(--line-1)", borderRadius: 10, padding: "10px 12px", lineHeight: 1.5, whiteSpace: "pre-line" }}>
          {state.opts.detail}
        </div>
      )}
    </Modal>
  ) : null;
  return [confirm, modal];
}

/* ─────────────────────────────────────────────
 * Toast
 * ───────────────────────────────────────────── */
export function Toast({ msg, onDone, ttl = 2500 }) {
  useEffect(() => {
    const t = setTimeout(onDone, ttl);
    return () => clearTimeout(t);
  }, [msg, ttl, onDone]);
  return <div className="ad-toast"><Icon.check />{msg}</div>;
}

/* ─────────────────────────────────────────────
 * Form helpers
 * ───────────────────────────────────────────── */
export function Hint({ text }) {
  if (!text) return null;
  return <span className="hint" title={text}>?</span>;
}
export const HelpTip = Hint;

export function Field({ label, hint, help, children, full }) {
  return (
    <div className={`ad-field${full ? " full" : ""}`} style={full ? { gridColumn: "1 / -1" } : null}>
      {label && (
        <label className="form-label">
          {label}
          {hint && <Hint text={hint} />}
        </label>
      )}
      {children}
      {help && <span className="help">{help}</span>}
    </div>
  );
}
export const LabeledField = ({ label, help, children, full }) => (
  <Field label={label} hint={help} full={full}>{children}</Field>
);

export function Option({ on, onClick, title, desc, warn, danger }) {
  return (
    <button type="button" className={`opt${on ? " on" : ""}${danger ? " danger" : ""}`} onClick={onClick}>
      <span className="box"><Icon.check /></span>
      <span>
        <span className="opt-t">{title}</span>
        {desc && <span className={`opt-d${warn ? " warn" : ""}`} style={{ display: "block" }}>{desc}</span>}
      </span>
    </button>
  );
}

/* ─────────────────────────────────────────────
 * Formatters
 * ───────────────────────────────────────────── */
export function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" });
}
export function fmtDateShort(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-GB");
}
export function fmtBytes(n) {
  const bytes = Number(n || 0);
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
export function fmtCurrency(amount) {
  const n = Number(amount || 0);
  return `Rs. ${n.toLocaleString("en-PK", { maximumFractionDigits: 2 })}`;
}

/** Date cell matching the artifact: date on top line, HH:MM:SS small below. */
export function DateCell({ v, iso }) {
  const text = v != null ? v : (iso ? fmtDate(iso) : "");
  if (!text) return <span className="t-muted">—</span>;
  const [d, t] = String(text).split(",").map((s) => s.trim());
  return <span className="t-date">{d}{t && <small>{t}</small>}</span>;
}

/** Module chips (H/S/L/R). */
export function Modules({ list }) {
  if (!list || !list.length) return <span className="t-muted">—</span>;
  const names = { H: "Hospital", S: "Store", L: "Lab", R: "Radiology" };
  return <span className="mods">{list.map((m) => <span key={m} className={`mod ${m}`} title={names[m]}>{m}</span>)}</span>;
}

/** Device-count progress bar + "N/M" number. */
export function DeviceBar({ used = 0, max = 1 }) {
  const pct = max > 0 ? Math.min(100, (used / max) * 100) : 0;
  const over = used > max;
  return (
    <span className="dev-bar">
      <span className="track"><span className={`fill${over ? " over" : ""}`} style={{ width: `${over ? 100 : pct}%` }} /></span>
      <span className="t-num" style={{ fontWeight: 700 }}>{used}<span className="t-muted"> / {max}</span></span>
    </span>
  );
}

/** Colored avatar from initials. */
const AV_COLORS = ["#0EA5A4", "#2563EB", "#7C3AED", "#DB2777", "#D97706", "#16A34A", "#0891B2"];
export function Avatar({ name, email, id, active = true, size }) {
  const n = String(name || email || "?").trim();
  const initials = n.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase() || "?";
  const seed = (id != null ? Number(id) : n.length) % AV_COLORS.length;
  const style = { background: AV_COLORS[seed], opacity: active ? 1 : 0.45 };
  if (size) { style.width = size; style.height = size; style.fontSize = Math.round(size * 0.4); }
  return <div className="avatar" style={style}>{initials}</div>;
}

/* ─────────────────────────────────────────────
 * Infinite list
 * ───────────────────────────────────────────── */
export function useInfiniteList(baseUrl) {
  const api = useAdminApi();
  const [items, setItems] = useState([]);
  const [nextUrl, setNextUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [total, setTotal] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const inFlight = useRef(false);

  useEffect(() => {
    setItems([]);
    setNextUrl(baseUrl);
    setInitialized(false);
    setTotal(null);
    setErr(null);
  }, [baseUrl]);

  const loadMore = useCallback(() => {
    if (!nextUrl || inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    let fetchUrl = nextUrl;
    if (typeof fetchUrl === "string" && /^https?:\/\//i.test(fetchUrl)) {
      try {
        const u = new URL(fetchUrl);
        fetchUrl = (u.pathname + u.search).replace(/^\/api\//, "");
      } catch { /* fall back */ }
    }
    api.get(fetchUrl)
      .then((r) => {
        const data = r.data;
        if (Array.isArray(data)) {
          setItems(data); setNextUrl(null); setTotal(data.length);
        } else {
          setItems((prev) => [...prev, ...(data.results || [])]);
          setNextUrl(data.next || null);
          setTotal(data.count ?? null);
        }
        setInitialized(true);
      })
      .catch(setErr)
      .finally(() => { inFlight.current = false; setLoading(false); });
  }, [api, nextUrl]);

  useEffect(() => {
    if (!initialized && nextUrl && !inFlight.current) loadMore();
  }, [initialized, nextUrl, loadMore]);

  return { items, loadMore, loading, err, hasMore: !!nextUrl, total, initialized };
}

export function InfiniteSentinel({ onReached, loading, hasMore }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!hasMore || !ref.current) return;
    const io = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) onReached(); },
      { rootMargin: "200px" }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, [onReached, hasMore]);
  if (!hasMore && !loading) return null;
  return (
    <div ref={ref} style={{ padding: "16px 0", textAlign: "center", fontSize: 12.5, color: "var(--fg-4)", fontWeight: 500 }}>
      {loading ? "Loading more…" : "Scroll to load more"}
    </div>
  );
}
